import {
  type FunctionComponent,
  type ReactNode,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  // Input,
  Link,
  type Theme,
  useTheme,
} from '@dr.pogodin/react-utils';

import Throbber from 'components/Throbber';
import VimeoVideoPreview from 'components/VimeoVideoPreview';

import { deduceVideoUrlKind } from 'utils';

import IconTrash from 'assets/images/icon-trash-16px.svg';

import IconVideo from 'assets/images/icon-video-48px.svg';

// import erroredInputTheme from './errored-input.scss';
// import inputTheme from './input.scss';
import './style.scss';

import defaultTheme from './theme.scss';

// TODO: This interface comes from the original component use for upload
// of video content by practitioners, thus it makes less sense for other
// video upload cases... though, it should work as this for now, thus no
// need to spend time right away to re-engineer it.
type PropsT = {
  error?: string;
  localVideo: File | string | undefined;
  onRemoveExisting?: () => void;
  setLocalVideo: (video: File | string | undefined) => void;
  theme?: Theme<'container' | 'dropArea' | 'error' | 'errorMessage'>;
  video: {
    isReadyToBePlayed: boolean;
    url: string;
  } | null;
};

const AdminVideoUpload: FunctionComponent<PropsT> = ({
  error,
  localVideo,
  onRemoveExisting,
  setLocalVideo,
  theme,
  video,
}) => {
  const composed = useTheme('VideoUpload', defaultTheme, theme);

  // const importFromUrlRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  // const [importVideoUrl, setImportVideoUrl] = useState('');

  // const localVideoKind = typeof localVideo === 'string' && deduceVideoUrlKind(localVideo);

  // Set either when the existing video 404s on Vimeo (deleted directly there,
  // leaving a stale reference in our DB), or when the admin clicks the trash
  // icon on it - in both cases we just stop rendering it as "existing video".
  const [existingVideoCleared, setExistingVideoCleared] = useState(false);

  // Whenever the persisted video actually changes (e.g. after a new upload
  // is saved), forget any earlier not-found/cleared state for the old one.
  const [prevVideoUrl, setPrevVideoUrl] = useState(video?.url);
  if (video?.url !== prevVideoUrl) {
    setPrevVideoUrl(video?.url);
    setExistingVideoCleared(false);
  }

  const effectiveVideo = existingVideoCleared ? null : video;

  const videoPreview: ReactNode = useMemo(() => {
    if (localVideo) {
      if (typeof localVideo === 'string') {
        const kind = deduceVideoUrlKind(localVideo);
        if (kind === 'VIMEO') {
          return (
            <VimeoVideoPreview
              placeholder={<IconVideo styleName="icon" />}
              url={localVideo}
            />
          );
        }
      } else if (localVideo instanceof File) {
        const url = URL.createObjectURL(localVideo);
        return (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <video controls key={url} styleName="videoPreview">
            <source src={url} />
          </video>
        );
      }
    } else if (effectiveVideo?.url) {
      const placeholder = (
        <div styleName="uploadedAndIsBeingProcessed">
          <Throbber />
          {effectiveVideo.isReadyToBePlayed ? null
            : <div>Uploaded and is being processed</div>}
        </div>
      );

      // TODO: If somebody really pays attention, there is a slightly visible
      // artifact - the throbber position is reset when the video status changes
      // to ready to play, and the original placeholder is replaced by the same
      // placeholder rendered via the preview. Not a big deal, but if we are
      // pedantic, we should render <VimeoVideoPreview> right away, updating it
      // for the possibility to get no URL (and show the placeholder then),
      // and then mounting the player once it gets the URL (so that there is
      // no need to re-mount the placeholder while the player is being prepped).
      return effectiveVideo.isReadyToBePlayed
        ? (
          <VimeoVideoPreview
            onError={() => {
              setExistingVideoCleared(true);
            }}
            placeholder={placeholder}
            url={effectiveVideo.url}
          />
        )
        : placeholder;
    }

    // This is the default placeholder icon, which also acts as the fallback
    // if the video passed in does not match any supported case we can render.
    return <IconVideo styleName="icon" />;
  }, [effectiveVideo, localVideo]);

  let containerClassName = composed.container ?? '';
  if (error !== undefined) containerClassName += ` ${composed.error ?? ''}`;

  return (
    <div className={containerClassName}>
      <div
        className={composed.dropArea}
        onDragOver={(e) => {
          e.preventDefault();
        }}
        onDrop={(e) => {
          const [file] = e.dataTransfer.files;
          setLocalVideo(file?.type.startsWith('video/') ? file : undefined);
          // setImportVideoUrl('');
          e.preventDefault();
        }}
      >
        {
          localVideo ?? effectiveVideo?.url ? (
            <Link
              onClick={() => {
                if (videoInputRef.current) videoInputRef.current.value = '';
                setLocalVideo(undefined);

                // Clicking trash with no pending local file means we're
                // removing the existing (persisted) video, e.g. because it's
                // no longer found on Vimeo - clear it so the drop area
                // reappears, ready for a replacement to be uploaded.
                if (!localVideo && video?.url) {
                  setExistingVideoCleared(true);
                  onRemoveExisting?.();
                }
              }}
              to=""
            >
              <IconTrash
                styleName="trashIcon"
              />
            </Link>
          ) : null
        }
        {videoPreview}
        {localVideo ?? effectiveVideo?.url ? null : (
          <div styleName="text">
            <p styleName="p1">Drag and drop your video file here</p>
            <p styleName="p2">
              Or &zwnj;
              <Link
                onClick={() => {
                  if (!videoInputRef.current) throw Error('Missing reference');
                  videoInputRef.current.click();
                }}
                to=""
              >
                click here
              </Link>
              &zwnj; to search the file in your computer
            </p>
            <p styleName="p3">
              For best results, use MP4 or MOV files in a HD resolution
              (1920 &times; 1080 pixels)
            </p>
          </div>
        )}
        <input
          accept="video/*, .mov"
          onChange={(e) => {
            const v = e.target.files?.[0];
            if (!v) throw Error('Internal error');
            // setImportVideoUrl('');
            setLocalVideo(v);
          }}
          ref={videoInputRef}
          styleName="hiddenVideoInput"
          type="file"
        />
      </div>
      {error ? (
        <div styleName="errorMessage">
          {error}
        </div>
      ) : null}
      {/*
      <div>
        <div styleName="importAsUrlLabel">Import your video from Vimeo</div>
        <Input
          // TODO: Disabled for now - it turns out to be problematic to pull
          // Vimeo videos via URL, as Vimeo does not provide direct links to
          // third-party videos.
          disabled

          onBlur={() => {
            setLocalVideo(importVideoUrl);
          }}
          onChange={(e) => {
            setImportVideoUrl(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') importFromUrlRef.current!.blur();
          }}
          placeholder="https://"
          ref={importFromUrlRef}
          theme={typeof localVideo === 'string' && !localVideoKind ? erroredInputTheme : inputTheme}
          value={importVideoUrl}
        />
      </div>
      */}
    </div>
  );
};

export default AdminVideoUpload;
